import { Flex, Skeleton } from '@chakra-ui/react'
import { Copy, ThumbsDown, ThumbsUp } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { OverallSentimentSection } from '~/pages/Calls/CallDetails/sections/OverallSentimentSection'
import { Size } from '~/types/ThemeTypes'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { CallInstance } from '@mangovoice/types'
import { ApiError, VoicemailInstance } from '@mangovoice/types'

import { getTranscriptSummaryNotAvailable } from '../CallDetailsAnalyticsContent'
import { VoicemailTranscriptSection } from './VoicemailTranscriptSection'

export type CallTranscriptSummarySectionProps = {
  overallCustomerScore: number
  overallAgentScore: number
  summaryText?: string
  isLoading: boolean
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
  onVoicemailClick?: () => void
  callInstance?: CallInstance
}

export const CallTranscriptSummarySection = ({
  summaryText,
  overallCustomerScore,
  overallAgentScore,
  isLoading,
  voicemail,
  voicemailError,
  isVoicemailLoading,
  onVoicemailClick,
  callInstance,
}: CallTranscriptSummarySectionProps) => {
  const { t } = useTranslation()
  const { logger } = useCloudwatchLogger('CallTranscriptSummarySection')
  const { toastError, toastSuccess } = useToastAlert()
  const { copyToClipboard } = useClipboard({
    onSuccess: () => toastSuccess(t('callDetails.toast.summaryCopied')),
    onFailure: () => toastError(t('callDetails.toast.summaryCopyFailed')),
  })

  const [hasClickedFeedback, setHasClickedFeedback] = useState(false)

  const transcriptSummaryNotAvailable = getTranscriptSummaryNotAvailable(t)
  const hasSummary = summaryText !== transcriptSummaryNotAvailable

  const handleThumbsUp = () => {
    toastSuccess(t('callDetails.toast.feedbackThanks'))
    if (hasClickedFeedback) return
    setHasClickedFeedback(true)
    try {
      logger?.info('summary_fb_thumbs_up')
    } catch (error) {
      console.error('Failed to log Thumbs Up:', error)
      toastError(t('callDetails.toast.feedbackThumbsUpFailed'))
    }
  }

  const handleThumbsDown = () => {
    toastSuccess(t('callDetails.toast.feedbackThanks'))
    if (hasClickedFeedback) {
      return
    }

    setHasClickedFeedback(true)

    try {
      logger?.info('summary_fb_thumbs_down')
    } catch (error) {
      console.error('Failed to log Thumbs Down:', error)
      toastError(t('callDetails.toast.feedbackThumbsDownFailed'))
    }
  }

  const handleCopySummary = async () => {
    try {
      if (summaryText) {
        copyToClipboard(summaryText)
      } else {
        toastError(t('callDetails.toast.noSummary'))
      }
    } catch (error) {
      console.error('Failed to copy text: ', error)
    }
  }

  return (
    <Flex
      gap="0.5rem"
      width="full"
      flexDirection="column"
      position="relative"
      css={{
        '&:hover > * > .quick-action-buttons': { opacity: '100%' },
        '& > * > .quick-action-buttons:has(:focus-visible)': {
          opacity: '100%',
        },
        '@media (hover: none)': {
          '& > * > .quick-action-buttons': { opacity: '100%' },
        },
      }}
    >
      {isLoading ? (
        <>
          <Skeleton h="15rem" />
          <Flex w="full" h="3rem" gap="1.25rem">
            <Skeleton flexGrow="1" />
            <Skeleton flexGrow="1" />
          </Flex>
        </>
      ) : (
        <>
          <Flex
            justifyContent="space-between"
            color="fg.default"
            {...textStyles[textStyleKeys.Body1_Bold]}
            marginBottom="0.3rem"
          >
            {t('callDetails.summary.title')}
            {hasSummary && (
              <Flex
                alignItems="center"
                className="quick-action-buttons"
                opacity="0"
                position="absolute"
                right="0"
                gap="0.5rem"
              >
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={Copy}
                  onClick={handleCopySummary}
                  tooltipText={t('callDetails.tooltip.copyToClipboard')}
                />
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={ThumbsUp}
                  onClick={handleThumbsUp}
                  tooltipText={t('callDetails.tooltip.accurate')}
                />
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={ThumbsDown}
                  tooltipText={t('callDetails.tooltip.inaccurate')}
                  onClick={handleThumbsDown}
                />
              </Flex>
            )}
          </Flex>
          <Flex
            color={
              callInstance?.transcription_status === 'IN_PROGRESS'
                ? 'fg.muted'
                : 'fg.default'
            }
            {...textStyles[textStyleKeys.Body1]}
          >
            {summaryText && callInstance?.transcription_status !== 'IN_PROGRESS'
              ? summaryText
              : callInstance?.transcription_status === 'IN_PROGRESS'
                ? t('callDetails.summary.processing')
                : t('callDetails.summary.notFound')}
          </Flex>
          {(voicemail || voicemailError) && (
            <VoicemailTranscriptSection
              voicemail={voicemail}
              voicemailError={voicemailError}
              isVoicemailLoading={isVoicemailLoading}
              onVoicemailClick={onVoicemailClick}
            />
          )}

          {callInstance?.transcription_status !== 'IN_PROGRESS' && (
            <OverallSentimentSection
              overallCustomerScore={overallCustomerScore}
              overallAgentScore={overallAgentScore}
              marginTop="0.5rem"
            />
          )}
        </>
      )}
    </Flex>
  )
}
